import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  TituloDeSecao,
} from '../componentes/base';
import { dataHora, plural } from '../util/formato';
import { useSessao } from '../sessao/SessaoProvedor';
import { ModalNovaColaboradora } from '../componentes/NovaColaboradora';

interface Colaboradora {
  readonly id: string;
  readonly nome: string;
  readonly login: string;
  readonly papel: string;
  readonly ativo: boolean;
  readonly ultimoAcesso: string | null;
  readonly devedoresDesignados: number;
}

interface Evento {
  readonly id: string;
  readonly ocorridoEm: string;
  readonly acao: string;
  readonly detalhe: string | null;
  readonly autor: { nome: string } | null;
  readonly autorExterno: string | null;
}

export function PaginaEquipe() {
  const { usuaria } = useSessao();
  const clienteConsulta = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [cadastrando, setCadastrando] = useState(false);

  const { data: equipe, isLoading } = useQuery({
    queryKey: ['equipe'],
    queryFn: () => api.buscar<Colaboradora[]>('/usuarios'),
  });

  const { data: auditoria } = useQuery({
    queryKey: ['auditoria'],
    queryFn: () => api.buscar<Evento[]>('/auditoria', { limite: 60 }),
  });

  const alternarAcesso = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) =>
      ativo
        ? api.criar(`/usuarios/${id}/reativar`, {})
        : api.alterar(`/usuarios/${id}`, { ativo: false }),
    onSuccess: () => {
      setErro(null);
      void clienteConsulta.invalidateQueries({ queryKey: ['equipe'] });
    },
    onError: (e) => setErro(e instanceof FalhaDaApi ? e.message : 'Falha ao alterar o acesso.'),
  });

  if (isLoading || !equipe) return <Carregando />;

  return (
    <>
      <CabecalhoDaPagina
        titulo="Equipe do escritório"
        descricao={`${plural(equipe.filter((u) => u.ativo).length, 'colaboradora com acesso', 'colaboradoras com acesso')}`}
        acoes={
          <Botao tipo="primario" onClick={() => setCadastrando(true)}>
            Nova colaboradora
          </Botao>
        }
      />

      <ModalNovaColaboradora
        aberto={cadastrando}
        aoFechar={() => setCadastrando(false)}
      />

      {erro && (
        <div className="mb-5">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      <div className="mb-6">
        <Alerta>
          Todas têm <strong>acesso total</strong>. O login individual existe para registro
          de autoria — cada baixa, mudança de fase e conferência fica marcada com quem a
          praticou. Por isso ninguém é excluída, apenas <strong>desativada</strong>: o
          histórico precisa continuar legível anos depois.
        </Alerta>
      </div>

      <Cartao semPadding className="mb-6">
        <div className="rolagem-lateral">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                <th className="px-5 py-2.5 font-medium">Colaboradora</th>
                <th className="px-3 py-2.5 font-medium">Login</th>
                <th className="px-3 py-2.5 text-right font-medium">Casos designados</th>
                <th className="px-3 py-2.5 font-medium">Último acesso</th>
                <th className="px-3 py-2.5 font-medium">Situação</th>
                <th className="px-5 py-2.5 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {equipe.map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="px-5 py-3">
                    <span className="font-medium text-[color:var(--texto)]">{u.nome}</span>
                    {u.id === usuaria?.id && (
                      <span className="ml-2 text-[0.6875rem] text-[color:var(--texto-tenue)]">
                        você
                      </span>
                    )}
                    <p className="text-[0.6875rem] text-[color:var(--texto-tenue)]">
                      {u.papel === 'ADVOGADA' ? 'Advogada' : 'Assistente'}
                    </p>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-[color:var(--texto-suave)]">
                    {u.login}
                  </td>
                  <td className="tabular px-3 py-3 text-right">
                    {u.devedoresDesignados > 0 ? (
                      <Link
                        to={`/devedores?responsavelId=${u.id}`}
                        className="hover:text-[color:var(--acento)]"
                      >
                        {u.devedoresDesignados}
                      </Link>
                    ) : (
                      <span className="text-[color:var(--texto-tenue)]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap text-[color:var(--texto-suave)]">
                    {u.ultimoAcesso ? dataHora(u.ultimoAcesso) : 'nunca acessou'}
                  </td>
                  <td className="px-3 py-3">
                    {u.ativo ? <Ficha tom="recebido">Ativa</Ficha> : <Ficha tom="neutro">Sem acesso</Ficha>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {u.id !== usuaria?.id && (
                      <Botao
                        onClick={() => alternarAcesso.mutate({ id: u.id, ativo: !u.ativo })}
                        desabilitado={alternarAcesso.isPending}
                        className="px-2.5 py-1 text-xs"
                      >
                        {u.ativo ? 'Desativar' : 'Reativar'}
                      </Botao>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Cartao>

      <Cartao semPadding>
        <div className="p-5 pb-3">
          <TituloDeSecao auxiliar="últimas 60 ações">Registro de atividade</TituloDeSecao>
          <p className="text-sm text-[color:var(--texto-suave)]">
            Auditoria técnica — quem fez o quê no sistema.
          </p>
        </div>
        <ul className="max-h-[26rem] overflow-y-auto border-t">
          {(auditoria ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 border-b px-5 py-2 text-sm last:border-0">
              <span className="tabular w-36 shrink-0 text-xs text-[color:var(--texto-tenue)]">
                {dataHora(e.ocorridoEm)}
              </span>
              <span className="w-44 shrink-0 truncate text-xs text-[color:var(--texto-suave)]">
                {e.autor?.nome ?? e.autorExterno ?? 'Sistema'}
              </span>
              <span className="text-[color:var(--texto)]">
                {e.acao.replace(/_/g, ' ')}
              </span>
              {e.detalhe && (
                <span className="truncate text-xs text-[color:var(--texto-tenue)]">
                  {e.detalhe}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Cartao>
    </>
  );
}
